import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminSkeleton } from "@/components/FeedSkeleton";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/components/FileUpload";
import { videoPosterFor } from "@/lib/neutralPoster";
import { gradientCss } from "@/lib/gradients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, EyeOff, Trash2, Search, Film, Image as ImageIcon, Type, HeartHandshake } from "lucide-react";
import { usePager, PagerBar } from "@/components/admin/usePager";

const TABS = [
  { key: "posts", label: "Publications" },
  { key: "reels", label: "Reels" },
  { key: "stories", label: "Stories" },
  { key: "prayers", label: "Prières" },
];

const Media = ({ item, isStory }) => {
  const cls = "h-20 w-20 rounded-lg object-cover shrink-0";
  if (isStory) {
    if (item.media_type === "video" && item.media_url) return <video src={resolveMediaUrl(item.media_url)} poster={videoPosterFor(resolveMediaUrl(item.media_url))} className={cls} muted preload="metadata" />;
    if (item.media_url) return <img src={resolveMediaUrl(item.media_url)} alt="" className={cls} />;
    const grad = item.bg && gradientCss(item.bg);
    return <div className={`${cls} grid place-items-center`} style={{ background: grad || "var(--yalola-surface-alt)" }}><Type size={20} color={grad ? "#fff" : "var(--yalola-text-muted)"} /></div>;
  }
  if (item.images?.length) return <img src={resolveMediaUrl(item.images[0])} alt="" className={cls} />;
  if (item.video_url) return <video src={resolveMediaUrl(item.video_url)} poster={videoPosterFor(resolveMediaUrl(item.video_url))} className={cls} muted preload="metadata" />;
  const grad = item.bg && gradientCss(item.bg);
  return <div className={`${cls} grid place-items-center`} style={{ background: grad || "var(--yalola-surface-alt)" }}><Type size={20} color={grad ? "#fff" : "var(--yalola-text-muted)"} /></div>;
};

export const ContentsPanel = ({ canManage = true }) => {
  const [searchParams] = useSearchParams();
  const ctParam = searchParams.get("ct");
  const [tab, setTab] = useState(TABS.some((t) => t.key === ctParam) ? ctParam : "posts");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const contentPager = usePager(items);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const [preview, setPreview] = useState(null);
  const [counts, setCounts] = useState({});

  const load = useCallback((tabKey, query) => {
    setLoading(true);
    api.get(`/admin/content?type=${tabKey}${query ? `&q=${encodeURIComponent(query)}` : ""}`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadCounts = useCallback(() => {
    api.get("/admin/content/counts").then(({ data }) => setCounts(data || {})).catch(() => {});
  }, []);

  useEffect(() => { load(tab, ""); setQ(""); }, [tab, load]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const ctype = tab === "stories" ? "story" : tab === "prayers" ? "prayer" : "post";

  // Full real content for the review modal (real images / video with controls / text) —
  // so a moderator can actually SEE what they're about to hide or delete.
  const renderPreviewMedia = (it) => {
    if (tab === "stories") {
      if (it.media_type === "video" && it.media_url) return <video controls autoPlay src={resolveMediaUrl(it.media_url)} className="w-full max-h-[55vh] rounded-lg bg-black" data-testid="content-preview-video" />;
      if (it.media_url) return <img src={resolveMediaUrl(it.media_url)} alt="" className="w-full max-h-[55vh] object-contain rounded-lg" />;
      return <div className="rounded-lg p-10 text-center text-white text-lg font-medium" style={{ background: (it.bg && gradientCss(it.bg)) || "var(--yalola-surface-alt)" }}>{it.text || "Story texte"}</div>;
    }
    return (
      <>
        {(it.images || []).map((im, i) => <img key={i} src={resolveMediaUrl(im)} alt="" className="w-full rounded-lg object-contain max-h-[55vh]" />)}
        {it.video_url && <video controls autoPlay src={resolveMediaUrl(it.video_url)} className="w-full max-h-[55vh] rounded-lg bg-black" data-testid="content-preview-video" />}
        {it.audio_url && <audio controls src={resolveMediaUrl(it.audio_url)} className="w-full" />}
      </>
    );
  };

  const act = async (item, action) => {
    try {
      await api.post(`/admin/content/${ctype}/${item.id}/action`, { action });
      toast.success(action === "delete" ? "Contenu supprimé" : action === "hide" ? "Contenu masqué" : "Contenu réaffiché");
      setToDelete(null);
      load(tab, q);
      loadCounts();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-contents-panel">
      <div className="yalola-card p-4 space-y-3">
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          Tous les contenus publiés sur Yalola. Masque ou supprime manuellement ce qui ne respecte pas les valeurs de la plateforme — même quand la modération automatique n'a rien détecté.
        </p>
        <div className="flex gap-2 flex-wrap">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`pill ${tab === t.key ? "pill-primary" : "pill-outline"}`} data-testid={`contents-tab-${t.key}`}>
              {t.key === "reels" ? <Film size={13} className="inline mr-1" /> : t.key === "stories" ? <ImageIcon size={13} className="inline mr-1" /> : t.key === "prayers" ? <HeartHandshake size={13} className="inline mr-1" /> : null}
              {t.label}
              {t.key === "prayers" && counts.prayers > 0 && (
                <span
                  className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full inline-grid place-items-center text-[10px] font-bold align-middle"
                  style={tab === t.key ? { background: "#fff", color: "#DC2626" } : { background: "#DC2626", color: "#fff" }}
                  title="Prières visibles sur le mur (à surveiller)"
                  data-testid="contents-prayers-badge"
                >
                  {counts.prayers > 99 ? "99+" : counts.prayers}
                </span>
              )}
            </button>
          ))}
        </div>
        {tab !== "stories" && (
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
              <Input className="pl-9" placeholder={tab === "prayers" ? "Rechercher dans le texte des prières" : "Rechercher dans le texte des publications"} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(tab, q)} data-testid="contents-search" />
            </div>
            <Button className="pill-primary rounded-full" onClick={() => load(tab, q)} data-testid="contents-search-btn">Rechercher</Button>
          </div>
        )}
      </div>

      {loading && <AdminSkeleton />}
      {!loading && items.length === 0 && <div className="yalola-card p-8 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="contents-empty">Aucun contenu.</div>}

      <div className="grid md:grid-cols-2 gap-3">
        {contentPager.slice.map((it) => (
          <div key={it.id} className="yalola-card p-4" data-testid={`content-item-${it.id}`} style={{ opacity: it.is_hidden ? 0.65 : 1 }}>
            <div className="flex gap-3">
              <button type="button" onClick={() => setPreview(it)} className="shrink-0 rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-1 transition hover:opacity-90" style={{ "--tw-ring-color": "var(--yalola-primary)" }} title="Voir le contenu" data-testid={`content-preview-open-${it.id}`}>
                <Media item={it} isStory={tab === "stories"} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium line-clamp-2">{it.content || it.text || <span style={{ color: "var(--yalola-text-muted)" }}>Contenu multimédia</span>}</div>
                <div className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>
                  {it.author?.name || "—"} · {new Date(it.created_at).toLocaleString()}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {it.is_hidden && <span className="chip text-[10px]" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid={`content-hidden-${it.id}`}>Masqué</span>}
                  {tab === "prayers" && <span className="chip text-[10px]" style={{ background: "#EDE9FE", color: "#5B21B6" }} data-testid={`content-prayer-${it.id}`}>Prière{it.is_anonymous ? " · anonyme" : ""}</span>}
                  {it.is_reel && <span className="chip text-[10px]">Reel</span>}
                  {tab === "stories" && <span className="chip text-[10px]">{it.media_type === "video" ? "Story vidéo" : it.media_type === "text" ? "Story texte" : "Story photo"}</span>}
                  {typeof it.comment_count === "number" && it.comment_count > 0 && <span className="chip text-[10px]">{it.comment_count} comm.</span>}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setPreview(it)} data-testid={`content-view-${it.id}`}>
                <Eye size={14} className="mr-1" /> Voir
              </Button>
              {it.is_hidden ? (
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(it, "unhide")} disabled={!canManage} data-testid={`content-unhide-${it.id}`}>
                  <Eye size={14} className="mr-1" /> Réafficher
                </Button>
              ) : (
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(it, "hide")} disabled={!canManage} data-testid={`content-hide-${it.id}`}>
                  <EyeOff size={14} className="mr-1" /> Masquer
                </Button>
              )}
              <Button size="sm" variant="outline" className="rounded-full" style={{ color: "#B91C1C" }} onClick={() => setToDelete(it)} disabled={!canManage} data-testid={`content-delete-${it.id}`}>
                <Trash2 size={14} className="mr-1" /> Supprimer
              </Button>
            </div>
          </div>
        ))}
      </div>
      {!loading && <PagerBar page={contentPager.page} pages={contentPager.pages} total={contentPager.total} shown={contentPager.slice.length} onPage={contentPager.setPage} noun="contenu" testId="contents-pager" />}

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent data-testid="content-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce contenu ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le contenu sera définitivement supprimé (réactions, commentaires et enregistrements associés inclus). L'auteur sera notifié.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="content-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => toDelete && act(toDelete, "delete")} data-testid="content-delete-confirm" style={{ background: "#B91C1C" }}>
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="content-preview-dialog">
          <DialogHeader>
            <DialogTitle className="truncate">{preview?.author?.name || "Aperçu du contenu"}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              {(preview.content || preview.text) && <p className="text-sm whitespace-pre-wrap break-words">{preview.content || preview.text}</p>}
              {renderPreviewMedia(preview)}
              <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
                {preview.created_at ? new Date(preview.created_at).toLocaleString() : ""}{preview.is_hidden ? " · 🚫 Déjà masqué" : ""}
              </div>
              <div className="flex gap-2 pt-1">
                {preview.is_hidden ? (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => { act(preview, "unhide"); setPreview(null); }} disabled={!canManage} data-testid="content-preview-unhide">
                    <Eye size={14} className="mr-1" /> Réafficher
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => { act(preview, "hide"); setPreview(null); }} disabled={!canManage} data-testid="content-preview-hide">
                    <EyeOff size={14} className="mr-1" /> Masquer
                  </Button>
                )}
                <Button size="sm" variant="outline" className="rounded-full" style={{ color: "#B91C1C" }} onClick={() => { const t = preview; setPreview(null); setToDelete(t); }} disabled={!canManage} data-testid="content-preview-delete">
                  <Trash2 size={14} className="mr-1" /> Supprimer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
